import { useContext, useEffect, useRef, useState } from 'react';

import { WebSocketContext } from '../../../../providers/websocket-context/websocket-provider';
import { TokenDataRequest } from '../../../../providers/model/interfaces/token_data';
import { CurrencyTypeEnum } from '../../../../model/currency_type.enum';
import { DataLabelEnum } from '../../../../model/data_label.enum';
import { VolumeMultipleRequest } from '../../../../providers/model/volumeData';
import { IntervalEnum } from '../../../../model/interval.enum';
import { SourceTypeEnum } from '../../../../model/source_type.enum';
import { SUBSCRIPTIONS_KEY } from '../../../../constants/websocket-events';
import { normalizeTickerMessage } from '../../../../utils/ticker-normalizer';
import Utils from '../../../../utils/immersive_utils';
import './styles.scss';

// Purpose-built summary rows for the PP page, not the full Market Data
// ticker widget (which is wired into that page's own tab-selection/gallery
// machinery and isn't a quick reuse here).

// Optional implied-vol tiles (opt-in via `showVols`). ATMF / 25RR at 1W + 1M,
// polled off cm_time_series every 30s (the SVI feed only updates ~minutely, so a
// 30s poll is fine) — the live index tiles above are untouched by this. Deribit
// BTC/ETH only; both rows always show (market context, not per-flow-token).
interface VolQuery {
  key: string;
  label: string; // tile label
  volLabel: string; // cm_time_series label
  tenor: string;
  signed: boolean; // 25RR is signed
}
const VOL_QUERIES: VolQuery[] = [
  { key: 'ATMF_1W', label: 'ATMF 1W', volLabel: 'ATMF', tenor: '1W', signed: false },
  { key: 'ATMF_1M', label: 'ATMF 1M', volLabel: 'ATMF', tenor: '1M', signed: false },
  { key: '25RR_1W', label: '25RR 1W', volLabel: '25RR', tenor: '1W', signed: true },
  { key: '25RR_1M', label: '25RR 1M', volLabel: '25RR', tenor: '1M', signed: true },
];
const VOL_POLL_MS = 30000;

const fmtVol = (value: number | undefined, signed: boolean): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
  const sign = signed && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
};

// cm_time_series wants begin/end floored to the minute boundary, else the server
// silently returns empty.
const floorToMinute = (d: Date) => new Date(d.getTime() - (d.getTime() % 60000));
const utcMidnight = () => {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()));
};

const TOKENS = [
  { currency: CurrencyTypeEnum.BITCOIN, label: 'BTC' },
  { currency: CurrencyTypeEnum.ETHEREUM, label: 'ETH' },
];

// The underlying subscription ticks far more often than a summary band needs
// to visibly refresh — throttle accepted updates so it reads as "live" without
// flashing on every single tick.
const MIN_UPDATE_INTERVAL_MS = 4000;

type TickerKey = 'indexPrice' | 'priceChange' | 'high' | 'low' | 'fundingRate';

type TickerState = Record<TickerKey, number | undefined>;

const TILE_ORDER: { key: TickerKey; label: string }[] = [
  { key: 'indexPrice', label: 'Index' },
  { key: 'priceChange', label: '24h Change' },
  { key: 'high', label: '24h High' },
  { key: 'low', label: '24h Low' },
  { key: 'fundingRate', label: 'Funding/8h' },
];

const isValidNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const formatTile = (key: TickerKey, value: number | undefined): string => {
  if (!isValidNumber(value)) return '-';
  if (key === 'priceChange') return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  if (key === 'fundingRate') return `${value >= 0 ? '+' : ''}${(100 * value).toFixed(3)}%`;
  return Utils.formatNumber(value, 0);
};

const MetricTile = ({
  label,
  value,
  flashDirection,
}: {
  label: string;
  value: string;
  flashDirection: 'up' | 'down' | null;
}) => (
  <div className={`btc-ticker-tile${flashDirection ? ` flash-${flashDirection}` : ''}`}>
    <span className="btc-ticker-label">{label}</span>
    <span className="btc-ticker-value">{value}</span>
  </div>
);

const TickerRow = ({
  currency,
  label,
  subscriptionIdPrefix,
  showVols = false,
}: {
  currency: CurrencyTypeEnum;
  label: string;
  subscriptionIdPrefix: string;
  showVols?: boolean;
}) => {
  const { sendMessage, subscribeToChannel, unsubscribeFromChannel, isConnected } =
    useContext(WebSocketContext);

  // The prefix namespaces this instance's WS subscription id. The app mounts
  // every route's tabs at once (visibility-toggled, not unmounted), so a band
  // reused on another route (e.g. Options Flow) must use a DISTINCT prefix —
  // otherwise both instances register the same id in the shared handler registry
  // and clobber each other, leaving one route's band static (the f0ca7070 bug).
  const subscriptionId = `${subscriptionIdPrefix}_${label}`;

  // Invariant: the id MUST contain a SUBSCRIPTIONS_KEY substring, else the
  // open-channel response won't route back (isSubscriptionKey) and the band goes
  // silent. Dev-only guard so a future "clean" prefix can't reintroduce that bug.
  if (
    import.meta.env?.DEV &&
    !Object.values(SUBSCRIPTIONS_KEY).some((k) => subscriptionId.includes(k))
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `BtcTickerBand: subscriptionIdPrefix "${subscriptionIdPrefix}" contains no SUBSCRIPTIONS_KEY — ` +
        `open-channel responses won't route back and this band won't tick.`,
    );
  }

  const [data, setData] = useState<TickerState | null>(null);
  const previousRef = useRef<TickerState | null>(null);
  const lastAcceptedAtRef = useRef<number>(0);
  const [flashDirections, setFlashDirections] = useState<Partial<Record<TickerKey, 'up' | 'down'>>>(
    {},
  );
  const flashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isConnected) return;

    const request = new TokenDataRequest(subscriptionId, currency, DataLabelEnum.PERPETUAL);
    sendMessage(JSON.stringify(request));

    subscribeToChannel(subscriptionId, (response: unknown) => {
      const now = Date.now();
      if (now - lastAcceptedAtRef.current < MIN_UPDATE_INTERVAL_MS) return;

      const ticker = normalizeTickerMessage(response);
      if (!ticker.exchange) return;

      lastAcceptedAtRef.current = now;

      const next: TickerState = {
        indexPrice: ticker.index_price,
        priceChange: ticker.price_change,
        high: ticker.high,
        low: ticker.low,
        fundingRate: ticker.funding_rate,
      };

      const previous = previousRef.current;
      previousRef.current = next;
      setData(next);

      if (!previous) return;

      const directions: Partial<Record<TickerKey, 'up' | 'down'>> = {};
      TILE_ORDER.forEach(({ key }) => {
        const prevVal = previous[key];
        const nextVal = next[key];
        if (!isValidNumber(prevVal) || !isValidNumber(nextVal)) return;
        // Compare the rounded display string, not the raw float — a change
        // too small to show at this precision shouldn't trigger a flash.
        if (formatTile(key, prevVal) === formatTile(key, nextVal)) return;
        directions[key] = nextVal > prevVal ? 'up' : 'down';
      });
      if (Object.keys(directions).length === 0) return;

      setFlashDirections(directions);
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = window.setTimeout(() => setFlashDirections({}), 900);
    });

    return () => {
      unsubscribeFromChannel(subscriptionId);
    };
  }, [
    isConnected,
    sendMessage,
    subscribeToChannel,
    unsubscribeFromChannel,
    subscriptionId,
    currency,
  ]);

  useEffect(() => {
    return () => {
      if (flashTimeoutRef.current) window.clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // Implied-vol tiles (opt-in). SEPARATE from the live ticker subscription above
  // so the index/spot tiles keep ticking real-time — these just poll
  // cm_time_series every 30s (the vols only move ~minutely). WS fns held in a
  // ref so the poll doesn't re-fire on every provider re-render.
  const [vols, setVols] = useState<(number | undefined)[]>(() => VOL_QUERIES.map(() => undefined));
  const volWsRef = useRef({ sendMessage, subscribeToChannel, unsubscribeFromChannel });
  volWsRef.current = { sendMessage, subscribeToChannel, unsubscribeFromChannel };

  useEffect(() => {
    if (!showVols || !isConnected) return;
    let cancelled = false;
    // Outstanding one-shot poll ids, so cleanup can release any still awaiting a
    // response — an unmount mid-poll must not strand a handler-registry entry.
    const pending = new Set<string>();
    const releaseReqId = (reqId: string) => {
      pending.delete(reqId);
      const unsub = volWsRef.current.unsubscribeFromChannel;
      if (unsub) {
        try {
          unsub(reqId);
        } catch {
          /* ignore */
        }
      }
    };

    const fetchVols = () => {
      const ws = volWsRef.current;
      if (!ws.sendMessage || !ws.subscribeToChannel) return;
      const begin = utcMidnight();
      const end = floorToMinute(new Date());
      if (end.getTime() <= begin.getTime()) return;

      // One-shot cm_time_series id — MUST NOT contain a SUBSCRIPTIONS_KEY
      // substring, else the response is mis-routed to the subscription path.
      const reqId = `VOLBAND_TS_${label}_${Date.now()}`;
      pending.add(reqId);
      ws.subscribeToChannel(reqId, (raw: unknown) => {
        if (cancelled) return;
        const resp = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : undefined;
        if (resp?.error || !Array.isArray(resp?.result)) return;
        const rows = resp.result as unknown[][];
        if (rows.length === 0) return;
        // Columns 1..N are the vols in VOL_QUERIES order (col 0 = timestamp);
        // decimals -> percent. Current reading = the last row's value.
        const next = VOL_QUERIES.map((_q, idx) => {
          const vals = rows
            .map((r) => r[idx + 1])
            .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
          return vals.length ? vals[vals.length - 1] * 100 : undefined;
        });
        setVols(next);
        releaseReqId(reqId);
      });

      ws.sendMessage(
        JSON.stringify(
          new VolumeMultipleRequest(
            reqId,
            begin.toISOString(),
            end.toISOString(),
            IntervalEnum.ONE_MINUTE,
            SourceTypeEnum.DERIBIT,
            label,
            VOL_QUERIES.map((q) => ({ label: q.volLabel, tenor: q.tenor })),
            'SVI',
          ),
        ),
      );
    };

    fetchVols();
    const interval = setInterval(fetchVols, VOL_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
      [...pending].forEach(releaseReqId);
    };
  }, [showVols, isConnected, label]);

  const tickerTiles = TILE_ORDER.map(({ key, label: tileLabel }) => (
    <MetricTile
      key={key}
      label={key === 'indexPrice' ? `${label} Index` : tileLabel}
      value={formatTile(key, data?.[key])}
      flashDirection={flashDirections[key] ?? null}
    />
  ));

  // Without vols (PP): tiles are direct grid children of `.btc-ticker-row` —
  // UNCHANGED. With vols (Options Flow): split into a ticker group + a denser vol
  // group so 9 tiles pack into one line when wide, and the vol group wraps onto a
  // tidy second sub-line rather than overflowing when narrow (incl. the ~125%
  // content zoom). No horizontal scrollbar.
  if (!showVols) return <div className="btc-ticker-row">{tickerTiles}</div>;

  return (
    <div className="btc-ticker-row btc-ticker-row--with-vols">
      <div className="btc-ticker-group btc-ticker-group--ticker">{tickerTiles}</div>
      <div className="btc-ticker-group btc-ticker-group--vols">
        {VOL_QUERIES.map((q, idx) => (
          <MetricTile
            key={q.key}
            label={q.label}
            value={fmtVol(vols[idx], q.signed)}
            flashDirection={null}
          />
        ))}
      </div>
    </div>
  );
};

// subscriptionIdPrefix defaults to PP's original id namespace so the PP usages
// are byte-for-byte unchanged; a second mount (Options Flow) passes a distinct
// one. Must contain a SUBSCRIPTIONS_KEY substring (see the TickerRow guard).
// `showVols` (opt-in) appends the implied-vol tiles per row — PP omits it, so
// PP's rows and subscriptions are unchanged.
export const BtcTickerBand = ({
  subscriptionIdPrefix = 'CANDLE_SUBSCRIPTION_PP_TICKER',
  showVols = false,
}: {
  subscriptionIdPrefix?: string;
  showVols?: boolean;
} = {}) => {
  return (
    <div className="btc-ticker-band">
      {TOKENS.map(({ currency, label }) => (
        <TickerRow
          key={currency}
          currency={currency}
          label={label}
          subscriptionIdPrefix={subscriptionIdPrefix}
          showVols={showVols}
        />
      ))}
    </div>
  );
};
